import type { Language } from '@/context/LanguageContext';

export const MAX_IMAGE_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_AVATAR_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_MULTI_IMAGE_UPLOAD_COUNT = 10;
export const ACCEPTED_IMAGE_UPLOAD_TYPES = ['image/jpeg', 'image/png'] as const;

// Resize ceilings used when compression kicks in. Avatars never render at more
// than ~256px in the UI, so 1024 gives 4x DPR headroom without storing huge
// originals. Photos display larger (gallery, modals) — 2048 keeps detail.
export const AVATAR_MAX_DIMENSION = 1024;
export const PHOTO_MAX_DIMENSION = 2048;

export type ImageUploadCapability = 'single' | 'multiple' | 'avatar';
export type CompressionTarget = 'avatar' | 'photo';

export interface ImageUploadCopy {
  uploadPrompt: string;
  fileTypes: string;
  compressedNotice: string;
}

export interface ImageUploadFileLike {
  name: string;
  size: number;
  type: string;
}

export interface PreparedUpload {
  file: File;
  compressed: boolean;
  error: string | null;
}

const IMAGE_UPLOAD_COPY: Record<Language, Record<ImageUploadCapability, ImageUploadCopy>> = {
  en: {
    single: {
      uploadPrompt: 'Upload photo',
      fileTypes: 'JPG, JPEG or PNG — large photos compressed automatically',
      compressedNotice: 'Photo compressed to fit upload limit',
    },
    multiple: {
      uploadPrompt: 'Upload up to 10 photos',
      fileTypes: 'JPG, JPEG or PNG — large photos compressed automatically',
      compressedNotice: 'Photos compressed to fit upload limit',
    },
    avatar: {
      uploadPrompt: 'Upload icon',
      fileTypes: 'JPG, JPEG or PNG — large icons compressed automatically',
      compressedNotice: 'Icon compressed to fit upload limit',
    },
  },
  es: {
    single: {
      uploadPrompt: 'Sube una foto',
      fileTypes: 'JPG, JPEG o PNG — las fotos grandes se comprimen automáticamente',
      compressedNotice: 'Foto comprimida para cumplir el límite de subida',
    },
    multiple: {
      uploadPrompt: 'Sube hasta 10 fotos',
      fileTypes: 'JPG, JPEG o PNG — las fotos grandes se comprimen automáticamente',
      compressedNotice: 'Fotos comprimidas para cumplir el límite de subida',
    },
    avatar: {
      uploadPrompt: 'Sube un ícono',
      fileTypes: 'JPG, JPEG o PNG — los íconos grandes se comprimen automáticamente',
      compressedNotice: 'Ícono comprimido para cumplir el límite de subida',
    },
  },
};

export function getImageUploadCopy(
  language: Language,
  capability: ImageUploadCapability,
): ImageUploadCopy {
  return IMAGE_UPLOAD_COPY[language][capability];
}

export function isAcceptedImageUploadType(file: Pick<ImageUploadFileLike, 'type'>): boolean {
  return ACCEPTED_IMAGE_UPLOAD_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_UPLOAD_TYPES)[number]);
}

export function getUnsupportedImageUploadMessage(language: Language = 'en'): string {
  if (language === 'es') {
    return 'Solo se permiten imágenes JPG, JPEG o PNG.';
  }
  return 'Only JPG, JPEG, or PNG images are allowed.';
}

export function getOversizedImageUploadMessage(fileName: string, language: Language = 'en'): string {
  if (language === 'es') {
    return `El archivo "${fileName}" supera el límite de 5 MB`;
  }
  return `File "${fileName}" exceeds the 5 MB limit`;
}

export function getOversizedAvatarUploadMessage(fileName: string, language: Language = 'en'): string {
  if (language === 'es') {
    return `El archivo "${fileName}" supera el límite de 5 MB`;
  }
  return `File "${fileName}" exceeds the 5 MB limit`;
}

export function getCompressionFailedMessage(fileName: string, language: Language = 'en'): string {
  if (language === 'es') {
    return `El archivo "${fileName}" sigue siendo demasiado grande después de la compresión. Por favor elige una foto más pequeña.`;
  }
  return `File "${fileName}" is too large even after compression. Please choose a smaller photo.`;
}

export function getImageUploadLimitMessage(maxImages: number): string {
  return `Maximum ${maxImages} images allowed`;
}

export function validateImageUploadFile(file: ImageUploadFileLike, language: Language = 'en'): string | null {
  if (!isAcceptedImageUploadType(file)) {
    return getUnsupportedImageUploadMessage(language);
  }

  if (file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
    return getOversizedImageUploadMessage(file.name, language);
  }

  return null;
}

export function validateAvatarUploadFile(file: ImageUploadFileLike, language: Language = 'en'): string | null {
  if (!isAcceptedImageUploadType(file)) {
    return getUnsupportedImageUploadMessage(language);
  }

  if (file.size > MAX_AVATAR_UPLOAD_SIZE_BYTES) {
    return getOversizedAvatarUploadMessage(file.name, language);
  }

  return null;
}

export function validateImageUploadFiles<T extends ImageUploadFileLike>(files: T[], language: Language = 'en'): {
  validFiles: T[];
  error: string | null;
} {
  const invalidFile = files.find((file) => !isAcceptedImageUploadType(file));
  if (invalidFile) {
    return { validFiles: [], error: getUnsupportedImageUploadMessage(language) };
  }

  const oversizedFile = files.find((file) => file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES);
  if (oversizedFile) {
    return { validFiles: [], error: getOversizedImageUploadMessage(oversizedFile.name, language) };
  }

  return { validFiles: files, error: null };
}

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = source;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

async function stripImageOrientation(file: File): Promise<File> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    if (!width || !height) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    context.drawImage(image, 0, 0, width, height);

    const fallbackQuality = file.type === 'image/jpeg' ? 0.92 : undefined;
    const normalizedBlob = await canvasToBlob(canvas, file.type, fallbackQuality);
    if (!normalizedBlob) {
      return file;
    }

    return new File([normalizedBlob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } catch { // eslint-disable-line no-restricted-syntax -- EXIF strip fallback to original
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function normalizeImageUpload(file: File): Promise<File> {
  if (!isAcceptedImageUploadType(file) || typeof createImageBitmap === 'undefined') {
    return file;
  }

  return stripImageOrientation(file);
}

export async function normalizeImageUploads(files: File[]): Promise<File[]> {
  return Promise.all(files.map((file) => normalizeImageUpload(file)));
}

function getMaxDimension(target: CompressionTarget): number {
  return target === 'avatar' ? AVATAR_MAX_DIMENSION : PHOTO_MAX_DIMENSION;
}

function getMaxBytes(target: CompressionTarget): number {
  return target === 'avatar' ? MAX_AVATAR_UPLOAD_SIZE_BYTES : MAX_IMAGE_UPLOAD_SIZE_BYTES;
}

function scaledDimensions(width: number, height: number, maxDimension: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxDimension) {
    return { width, height };
  }
  const scale = maxDimension / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

function jpegFileName(originalName: string): string {
  return originalName.replace(/\.(png|jpe?g)$/i, '') + '.jpg';
}

const COMPRESSION_QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45, 0.4];

export async function compressImageUpload(file: File, target: CompressionTarget): Promise<File> {
  if (!isAcceptedImageUploadType(file)) {
    return file;
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;

    if (!sourceWidth || !sourceHeight) {
      return file;
    }

    const { width, height } = scaledDimensions(sourceWidth, sourceHeight, getMaxDimension(target));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    // JPEG output has no alpha channel; paint a white background first so a
    // transparent PNG doesn't render as black after re-encoding.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const targetBytes = getMaxBytes(target);
    // Quality descends from 0.85 → 0.4; lower quality produces a smaller blob.
    // First iteration that lands at or under the target cap wins and we break.
    let encodedBlob: Blob | null = null;
    for (const quality of COMPRESSION_QUALITIES) {
      const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      if (!blob) continue;
      encodedBlob = blob;
      if (blob.size <= targetBytes) break;
    }

    if (!encodedBlob) {
      return file;
    }

    return new File([encodedBlob], jpegFileName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch { // eslint-disable-line no-restricted-syntax -- compression fallback to original
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function prepareUpload(file: File, target: CompressionTarget, language: Language): Promise<PreparedUpload> {
  if (!isAcceptedImageUploadType(file)) {
    return { file, compressed: false, error: getUnsupportedImageUploadMessage(language) };
  }

  const targetBytes = getMaxBytes(target);
  if (file.size <= targetBytes) {
    const normalized = await normalizeImageUpload(file);
    return { file: normalized, compressed: false, error: null };
  }

  const compressed = await compressImageUpload(file, target);
  if (compressed.size > targetBytes) {
    return { file: compressed, compressed: false, error: getCompressionFailedMessage(file.name, language) };
  }

  return { file: compressed, compressed: true, error: null };
}

export function prepareAvatarUpload(file: File, language: Language = 'en'): Promise<PreparedUpload> {
  return prepareUpload(file, 'avatar', language);
}

// Kept as a public export for upcoming follow-up adoption in single-file
// flows (blog cover, provider avatar, etc.) — see MWBI-522 follow-up.
export function prepareImageUpload(file: File, language: Language = 'en'): Promise<PreparedUpload> {
  return prepareUpload(file, 'photo', language);
}

export async function prepareImageUploads(files: File[], language: Language = 'en'): Promise<{
  prepared: File[];
  anyCompressed: boolean;
  error: string | null;
}> {
  const results = await Promise.all(files.map((file) => prepareUpload(file, 'photo', language)));
  const firstError = results.find((r) => r.error !== null);
  if (firstError?.error) {
    return { prepared: [], anyCompressed: false, error: firstError.error };
  }
  return {
    prepared: results.map((r) => r.file),
    anyCompressed: results.some((r) => r.compressed),
    error: null,
  };
}
